import useEffectAsync, { CancellationToken } from '../../hooks/useEffectAsync';
import useCachedPromise from '../../hooks/useCachedPromise';
import useLabels from '../../hooks/useLabels';
import { AppRoute } from '../../router/Routing';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../../contexts/chat/ChatContext';
import { useNavigate } from 'react-router-dom';
import { ChatMessageResponse, GetChatResponse } from '../../models/ChatTypes';
import { ErrorHandler } from '../../contexts/error-handler/ErrorHandler';
import { useErrorHandlerContext } from '../../contexts/error-handler/ErrorContext';
import { NewChatButton } from '../../layouts/chat-view-layout/ChatViewLayout';

type Props = {
    active?: boolean;
    chatId?: string;
    onMessageResolve?: (messages: ChatMessageResponse[]) => void;
    reloadOnChatIdChange?: boolean;
};

const useLoadChatMessages = ({ active = true, chatId, reloadOnChatIdChange = true, onMessageResolve }: Props) => {
    const labels = useLabels();
    const navigate = useNavigate();
    const { getChat } = useChat();

    const [chat, setChat] = useState<GetChatResponse>();
    const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(active);
    const { errorId, registerError, removeError, getError } = useErrorHandlerContext();
    const error = getError(errorId);


    const getData = useCachedPromise(() => getChat(chatId!), [chatId]);
    const reload = useCallback(async (cancellationToken?: CancellationToken, silent = false, force = false) => {
        removeError(errorId);

        try {
            if (!silent) setIsLoadingChatHistory(true);

            const chat = await getData(force);
            if (cancellationToken?.cancelled) return;

            onMessageResolve?.(chat.messages);
            setChat(chat);
        } catch (err: any) {
            if (cancellationToken?.cancelled) return;

            registerError({
                [errorId]: {
                    type: 'modal',
                    headline: labels.historyErrorHeadline,
                    notification: {
                        headline: labels.historyErrorOccurredWhileLoading,
                        description: labels.chatNotFound,
                        details: err
                    },
                    onClose: () => navigate(AppRoute.chat)
                }
            });
        } finally {
            if (!silent) {
                setIsLoadingChatHistory(false);
            }
        }

    }, [removeError, errorId, getData, onMessageResolve, registerError, labels.historyErrorHeadline, labels.historyErrorOccurredWhileLoading, labels.chatNotFound, navigate]);

    const mountRef = useRef(false);
    const reloadRef = useRef(true);

    useEffectAsync(token => () => {
        if (!chatId || !active) return;
        const shouldReload = reloadRef.current;

        // Prevent reload case: Init ONCE only and stop. 
        if (!reloadOnChatIdChange) {
            if (shouldReload) reload(token);
            reloadRef.current = false;
            return;
        }

        // Reload case: Reload as long as 'shouldReload' or first mount.
        else if (mountRef.current || shouldReload) {
            mountRef.current = true;
            reload(token);
        };

    }, [labels, chatId, active, reload, reloadOnChatIdChange]);

    // Reset reloadRef when chatId changes if reloadOnChatIdChange is true
    useEffect(() => () => {
        if (reloadOnChatIdChange) reloadRef.current = true;
    }, [chatId, reloadOnChatIdChange]);

    const renderChatHistoryError = useMemo(() => <ErrorHandler.Modal id={errorId} footer={<NewChatButton />} />, [errorId]);

    return { chat, isLoadingChatHistory, chatHistoryError: error, renderChatHistoryError, reload };
};

export default useLoadChatMessages;